import { computed, ref } from 'vue';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type {
  CartDetailsItemResponse,
  CartDetailsResponse,
  PartialCaptureDecision,
} from '@/types/carts-transactions-api';

import { useTransactionSSE } from '@/composables/carts/useTransactionSSE';
import { convertOctoPrice } from '@/utils/utils';

export function usePartialFailure(
  cartsTransactionsApi: CartsTransactionsApi,
  cartDetails: { value: CartDetailsResponse | undefined },
  isItemExpired: (item: CartDetailsItemResponse) => boolean,
) {
  const failedBookingIds = ref<string[]>([]);
  const failureReasons = ref<Record<string, string>>({});
  const isProcessingDecision = ref(false);

  const isPartialFailure = computed(() => failedBookingIds.value.length > 0);
  const failedBookingIdSet = computed(() => new Set(failedBookingIds.value));

  const isAllFailedDueToExpiration = computed(() => {
    if (failedBookingIds.value.length === 0)
      return false;
    return failedBookingIds.value.every(id => failureReasons.value[id] === 'EXPIRED');
  });

  const { paymentStatus: decisionPaymentStatus, connect: connectDecisionSSE, disconnect: disconnectDecisionSSE } = useTransactionSSE(
    () => cartsTransactionsApi.getTransactionEventsUrl(),
  );

  function isItemFailed(item: { bookingId: string }): boolean {
    return failedBookingIdSet.value.has(item.bookingId);
  }

  const confirmedItemsTotal = computed(() => {
    if (!cartDetails.value)
      return 0;

    const totalInt = cartDetails.value.items
      .filter(item => !isItemFailed(item) && !isItemExpired(item))
      .reduce((total, item) => {
        const itemTotal = item.units.reduce((itemSum, unit) => {
          return itemSum + unit.retailPrice * unit.quantity;
        }, 0);
        return total + itemTotal;
      }, 0);

    return convertOctoPrice(totalInt, cartDetails.value.currencyPrecision);
  });

  function handlePartialFailure(data: { failedBookingIds: string[]; failureReasons?: Record<string, string> }) {
    failedBookingIds.value = data.failedBookingIds;
    failureReasons.value = data.failureReasons ?? {};
  }

  async function sendPartialDecision(decision: PartialCaptureDecision) {
    isProcessingDecision.value = true;

    try {
      await cartsTransactionsApi.sendPartialCaptureDecision(decision);
      connectDecisionSSE();
    }
    catch (err) {
      console.error(`[usePartialFailure] Error sending ${decision} decision:`, err);
      isProcessingDecision.value = false;
    }
  }

  function resetPartialFailureState() {
    disconnectDecisionSSE();
    isProcessingDecision.value = false;
    failedBookingIds.value = [];
    failureReasons.value = {};
    cartsTransactionsApi.clearStoredData();
    cartDetails.value = undefined;
  }

  return {
    failedBookingIds,
    failureReasons,
    isPartialFailure,
    isAllFailedDueToExpiration,
    isProcessingDecision,
    confirmedItemsTotal,
    decisionPaymentStatus,
    isItemFailed,
    handlePartialFailure,
    sendPartialDecision,
    resetPartialFailureState,
  };
}
